import { useEffect, useMemo, useState } from 'react'

import styles from './PlanCard.module.scss'

import { BusinessVariant } from '@/api/billing'
import Button from '@/components/Button'
import PurchaseTerms from '@/components/PurchaseTerms'
import RadioButton from '@/components/RadioButton'
import {
  BASE_FEATURES,
  ENTERPRISE_FEATURES,
  ENTERPRISE_NOTETAKER_NOTE,
  ORG_BASE_FEATURES,
  PRO_FEATURES,
} from '@/constants/planFeatures'
import { usePricing } from '@/hooks/usePricing'
import { PaymentSchedule, PlanType } from '@/types'

const GreenTick = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z"
        clipRule="evenodd"
      />
    </svg>
  )
}

interface PlanInformation {
  id: PlanType
  name: string
  monthlyType: PlanType
  annualType: PlanType
  featureList: string[]
  /** Muted line under the features, without a tick, for things the plan doesn't include. */
  footnote?: string
}

const PRO_PLAN: PlanInformation = {
  id: PlanType.Pro,
  name: 'Pro',
  monthlyType: PlanType.Pro,
  annualType: PlanType.Pro,
  featureList: [...BASE_FEATURES, ...PRO_FEATURES],
}

const TEAM_PLAN: PlanInformation = {
  id: PlanType.Team,
  name: 'Team',
  monthlyType: PlanType.Team,
  annualType: PlanType.Team,
  featureList: [...BASE_FEATURES, ...PRO_FEATURES],
}

const ENTERPRISE_PLAN: PlanInformation = {
  id: PlanType.Enterprise,
  name: 'Enterprise',
  monthlyType: PlanType.Enterprise,
  annualType: PlanType.Enterprise,
  featureList: ['Everything in Pro, plus:', ...ENTERPRISE_FEATURES],
}

const STANDALONE_ENTERPRISE_PLAN: PlanInformation = {
  id: PlanType.Enterprise,
  name: 'Enterprise',
  monthlyType: PlanType.Enterprise,
  annualType: PlanType.Enterprise,
  featureList: [...ORG_BASE_FEATURES, ...PRO_FEATURES, ...ENTERPRISE_FEATURES],
  footnote: ENTERPRISE_NOTETAKER_NOTE,
}

interface VariantToggleProps {
  value: BusinessVariant
  onChange: (variant: BusinessVariant) => void
}

interface PlanCardProps {
  plan: PlanInformation
  schedule: PaymentSchedule
  onClick: (planType: PlanType) => void
  isLoading?: boolean
  isCurrentPlan?: boolean
  buttonTitle?: string
  /** Current-plan view for subscribed orgs: existing customers keep their rate, so no price is shown. */
  hidePrice?: boolean
  /** Prices sourced outside the legacy plan map (the Business options); monthly rate + annual total. */
  priceOverride?: { monthly?: number; annualTotal?: number }
  /** Renders the dictation / notetaker radios on the Business card. */
  variantToggle?: VariantToggleProps
  /** Shows the MSA and DPA line under the button while the button buys; otherwise holds its height. */
  showPurchaseTerms?: boolean
  /** Holds the height of the MSA and DPA line so this card's button lines up with a card that shows it. */
  reservePurchaseTermsSlot?: boolean
}

const PRICING_TIMEOUT_MS = 10000 // 10 seconds

const PlanCard = ({
  plan,
  schedule,
  onClick,
  isLoading,
  isCurrentPlan,
  buttonTitle,
  hidePrice,
  priceOverride,
  variantToggle,
  showPurchaseTerms,
  reservePurchaseTermsSlot,
}: PlanCardProps) => {
  const { getPriceForPlan, formatPrice, isInitialized, fetchFailed, flag } = usePricing()
  const [hasPricingTimedOut, setHasPricingTimedOut] = useState(false)

  // Set a timeout for pricing to load
  useEffect(() => {
    if (isInitialized) {
      setHasPricingTimedOut(false)
      return
    }

    const timeoutId = setTimeout(() => {
      setHasPricingTimedOut(true)
    }, PRICING_TIMEOUT_MS)

    return () => clearTimeout(timeoutId)
  }, [isInitialized])

  const planMonthlyPrice = useMemo(
    () => getPriceForPlan(plan.monthlyType, PaymentSchedule.Monthly),
    [getPriceForPlan, plan.monthlyType]
  )
  const planAnnualPriceTotal = useMemo(
    () => getPriceForPlan(plan.annualType, PaymentSchedule.Annual),
    [getPriceForPlan, plan.annualType]
  )

  const monthlyPrice = priceOverride ? priceOverride.monthly : planMonthlyPrice
  const annualPriceTotal = priceOverride ? priceOverride.annualTotal : planAnnualPriceTotal

  // For annual pricing, the API returns the total yearly price, so divide by 12 to get monthly equivalent
  // formatPrice will handle decimal display appropriately based on the currency
  const annualPricePerMonth = annualPriceTotal !== undefined ? annualPriceTotal / 12 : undefined

  // The price to display based on schedule
  const displayPrice = schedule === PaymentSchedule.Monthly ? monthlyPrice : annualPricePerMonth

  // Floor + epsilon, never ceil: the badge must underclaim, and an exact 20% computes as 19.999…
  const discount =
    monthlyPrice && annualPricePerMonth ? Math.floor((1 - annualPricePerMonth / monthlyPrice) * 100 + 1e-9) : undefined

  // Format the price using custom formatter
  const formattedPrice = displayPrice !== undefined ? formatPrice(displayPrice) : undefined

  // A failed pricing fetch is not the same as a plan we deliberately don't price: never leave a
  // live buy button next to a price we couldn't load. The timeout affordance only arms while
  // uninitialized, and a failed fetch still initializes.
  const pricingUnavailable = !hidePrice && fetchFailed && displayPrice === undefined

  return (
    <div
      className={`${styles.planCard} ${isCurrentPlan ? styles.current : ''} ${variantToggle ? styles.hasBundleSelect : ''}`}
    >
      <h6 className={styles.heading}>
        {plan.name}
        {!hidePrice && schedule == PaymentSchedule.Annual && discount !== undefined && (
          <div className={styles.discount}>-{discount}%</div>
        )}
      </h6>
      {!hidePrice && (
        <div className={styles.price}>
          {formattedPrice ? (
            <span>
              {formattedPrice} per user/month{flag ? ` ${flag}` : ''}
            </span>
          ) : hasPricingTimedOut || pricingUnavailable ? (
            <span>Unable to load pricing. Please refresh.</span>
          ) : (
            <span>Loading pricing...</span>
          )}
          {schedule === PaymentSchedule.Annual && <div className={styles.annualDisclaimer}>Billed annually</div>}
        </div>
      )}
      <div className={styles.planDivider}></div>
      <div className={styles.featureList}>
        {plan.featureList.map((feature, index) => (
          <div key={index} className={styles.featureItem}>
            {/* "Everything in X, plus:" lines are headers, not features — no checkmark. */}
            {!feature.endsWith(', plus:') && <GreenTick />}
            {feature}
          </div>
        ))}
        {plan.footnote && <div className={styles.footnote}>{plan.footnote}</div>}
      </div>
      {variantToggle && (
        <div className={styles.bundleSelect}>
          <p id="business-bundle-type-label" className={styles.bundleSelectLabel}>
            Select bundle type:
          </p>
          <div role="radiogroup" aria-labelledby="business-bundle-type-label" className={styles.bundleRadios}>
            <RadioButton
              label="Dictation and Notetaker"
              selected={variantToggle.value === 'bundle'}
              onClick={() => variantToggle.onChange('bundle')}
            />
            <RadioButton
              label="Dictation only"
              selected={variantToggle.value === 'dictation_only'}
              onClick={() => variantToggle.onChange('dictation_only')}
            />
          </div>
        </div>
      )}
      <Button
        title={buttonTitle || 'Get started'}
        onClick={() => onClick(plan.id)}
        type="dark"
        isLoading={isLoading}
        isDisabled={isCurrentPlan || pricingUnavailable}
      />
      {showPurchaseTerms && !isCurrentPlan && !pricingUnavailable ? (
        <PurchaseTerms />
      ) : (
        (showPurchaseTerms || reservePurchaseTermsSlot) && <PurchaseTerms placeholder />
      )}
    </div>
  )
}

export default PlanCard
export { ENTERPRISE_PLAN, GreenTick, PRO_PLAN, STANDALONE_ENTERPRISE_PLAN, TEAM_PLAN }
export type { PlanInformation }
